import { apiService } from './api';

export interface SchoolSettings {
  timezone: string;
  dateFormat: string;
  currency: string;
  academicYearStart: number;
  /** Day of month (1-28) that fee installments snap to. Default 1. */
  feeBillingDay?: number;
}

export interface School {
  _id: string;
  name: string;
  slug: string;
  code: string; // School code for user ID generation (e.g., ABC, DPS)
  email: string;
  phone?: string;
  address?: string;
  logo?: string;
  billing?: {
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    address?: string;
  };
  settings: SchoolSettings;
  isActive: boolean;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  // Populated stats
  userCount?: number;
  admissionCount?: number;
  studentCount?: number;
}

export interface SupportedTimezones {
  [key: string]: string;
}

export interface CreateSchoolData {
  name: string;
  code: string; // School code for user ID generation
  email: string;
  phone?: string;
  address?: string;
  logo?: File;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  planId?: string;
  isTrial?: boolean;
  trialDays?: number;
}

// Shape returned by the paginated list endpoint.
export interface SchoolListResponse {
  schools: School[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Admin user as returned by the admin-list endpoint. Email is the login
// identity and is NOT editable through the admin-update endpoint.
export interface SchoolAdmin {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  roleId?: { _id: string; name: string } | string;
}

export const schoolService = {
  // Paginated, searchable list. The backend returns `userCount` and
  // `studentCount` per school via a single aggregation — no per-row queries.
  getSchools: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }) => {
    const response = await apiService.get<SchoolListResponse>('/super-admin/schools', params);
    return response.data as SchoolListResponse;
  },

  // Get single school
  getSchool: async (id: string) => {
    const response = await apiService.get<School>(`/super-admin/schools/${id}`);
    return response.data;
  },

  // Atomic create — backend wraps school + roles + subscription + admin in a
  // single transaction. Server pre-validates the admin email so we no longer
  // need the two-call dance that could orphan a school.
  createSchool: async (data: CreateSchoolData) => {
    const slug = data.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const response = await apiService.post<any>('/super-admin/schools', {
      name: data.name,
      code: data.code.toUpperCase(),
      slug,
      email: data.email,
      phone: data.phone,
      address: data.address,
      planId: data.planId,
      isTrial: data.isTrial,
      trialDays: data.trialDays,
      adminName: data.adminName,
      adminEmail: data.adminEmail,
      adminPassword: data.adminPassword
    });
    return response.data?.school;
  },

  // Update school
  updateSchool: async (id: string, data: Partial<School>) => {
    const response = await apiService.patch<School>(`/super-admin/schools/${id}`, data);
    return response.data;
  },

  // Delete school
  deleteSchool: async (id: string) => {
    const response = await apiService.patch<School>(`/super-admin/schools/${id}/status`, { isActive: false });
    return response.data;
  },

  // School admins — used by the drawer's Admin section.
  listAdmins: async (schoolId: string): Promise<SchoolAdmin[]> => {
    const r = await apiService.get<SchoolAdmin[]>(`/super-admin/schools/${schoolId}/admins`);
    return (r.data || []) as SchoolAdmin[];
  },
  // Email is immutable — passing it is silently ignored by the backend.
  updateAdmin: async (schoolId: string, userId: string, data: { name?: string; phone?: string }) => {
    const r = await apiService.patch<SchoolAdmin>(`/super-admin/schools/${schoolId}/admins/${userId}`, data);
    return r.data as SchoolAdmin;
  },
  resetAdminPassword: async (schoolId: string, userId: string, newPassword: string) => {
    await apiService.post(`/super-admin/schools/${schoolId}/admins/${userId}/reset-password`, { newPassword });
  },
  createAdmin: async (schoolId: string, data: { name: string; email: string; password: string; phone?: string }) => {
    const r = await apiService.post<SchoolAdmin>(`/super-admin/schools/${schoolId}/admin`, data);
    return r.data as SchoolAdmin;
  },

  // Get current user's school (use user's school from auth context instead)
  getMySchool: async () => {
    throw new Error('Use auth context to get current user school');
  },

  // Get school settings (for school admins)
  getSchoolSettings: async () => {
    const response = await apiService.get<{
      settings: SchoolSettings;
      supportedTimezones: SupportedTimezones;
    }>('/admin/settings');
    return response.data;
  },

  // Update school settings (for school admins)
  updateSchoolSettings: async (settings: Partial<SchoolSettings>) => {
    const response = await apiService.patch<SchoolSettings>('/admin/settings', settings);
    return response.data;
  },
};
